/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { HierarchicalEntityChild } from 'src/luis/projects/entities/types/HierarchicalEntity';

export type HierarchicalEntityChildTableCommandBarProps = TableCommandBarProps<HierarchicalEntityChild> & {
    /**
     * A callback for deleting entity children.
     */
    onDeleteChildren?: () => boolean | void;

    /**
     * User role to determine the actions
     */
    userRole: string;

    /**
     * User role to show in LuisTooltip message
     */
    displayedUserRole: string;
};

const messages = defineMessages({
    deleteChildButton: {
        id: 'entities.hierarchicalEntityChildTableCommandBar.deleteChildButton',
        defaultMessage: 'Delete'
    }
});

/**
 * Renders a command bar for the hierarchical entities children.
 *
 * @param props The input properties for the hierarchical children table.
 */
export const HierarchicalEntityChildTableCommandBar = (props: HierarchicalEntityChildTableCommandBarProps) => {
    const { selectedItems, onDeleteChildren, userRole, displayedUserRole } = props;
    const { localize } = useLocalization();

    const { canDeleteData } = mapUserRoleToUserActions(userRole);

    const items: ICommandBarItemProps[] = commandBarItemSelectionManager(
        [
            {
                key: 'delete',
                onRender: (item: ICommandBarItemProps) => (
                    <RBACTooltip
                        hidden={canDeleteData || item.disabled}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="deleteButtonLuisToolTip"
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                onClick={onDeleteChildren}
                                iconProps={{ iconName: 'Delete' }}
                                data-automation-id="deleteButton"
                                text={localize(messages.deleteChildButton)}
                                name={localize(messages.deleteChildButton)}
                                disabled={!canDeleteData || item.disabled}
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                )
            }
        ],
        [],
        ['delete'],
        ['delete'],
        selectedItems.length
    );

    return <LuisCommandBar items={items} />;
};
